/* eslint-disable no-case-declarations */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Iframe from 'react-iframe';
import { ModalBody, Spinner, Col, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import styled from 'styled-components';

import useApi from 'hooks/useApi';
import { setCalendarAccount } from 'redux/actions/calendarActions';

import { ReactComponent as CalendarCheckIcon } from 'assets/icon-calendar-check.svg';
import { ReactComponent as CalendarIcon } from 'assets/icon-calendar.svg';
import { ReactComponent as RefreshIcon } from 'assets/icon-rotate.svg';
import { ReactComponent as AddIcon } from 'assets/icon-add-round.svg';

import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import { Modal } from 'shared/components/modal/Modals';
import BackgroundBox from 'shared/components/BackgroundBox';
import { LabelIcon } from 'shared/components/Labels';
import { Button, ButtonLabelIcon } from 'shared/components/Buttons';
import { colors, fonts, fontSizes } from 'shared/defaultTheme';

const CalendarSectionCard = ({ className }) => {
  const { accountApi, lexonApi } = useApi();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const theme = useSelector((state) => state.theme);
  const { entradaUsuario } = useSelector((state) => state.auth);
  const { calendarAccount } = useSelector((state) => state.calendar);
  const { mainColor } = theme;
  const [openFormModal, setOpenFormModal] = useState(false);
  const [indexCalender, setIndexCalendar] = useState(0);
  const [calendarUrl, setCalendarUrl] = useState('');
  const [calendarEventUrl, setCalendarEventUrl] = useState('');
  const [imapCalendar, setImapCalendar] = useState(false); // Remove when nextcloud is launched
  const [, getAccountsDefault] = accountApi(
    {
      url: `accounts/auth/usermail/${entradaUsuario}/account/default`,
      method: 'GET',
    },
    { manual: true }
  );

  const [{ loading: tokenLexonLoading }, getTokenLexon] = lexonApi(
    {
      method: 'PUT',
    },
    { manual: true }
  );

  const getToken = async () => {
    const {
      data: { data },
    } = await getTokenLexon({
      data: {
        idClienteNavision: entradaUsuario,
      },
      url: `utilsauth/Lexon/token/lexon?addTerminatorToToken=true`,
    });
    return data;
  };

  const loadAccount = async () => {
    try {
      const {
        data: { data },
      } = await getAccountsDefault();
      if (data) {
        const defaultCalendar = {
          provider: data.provider,
          account: data.email,
        };
        const calendarConfig = data?.configCalendar?.provider
          ? data.configCalendar
          : defaultCalendar;
        if (calendarConfig.provider === 'IMAP') {
          // Remove this when NEXTCLOUD is launched
          setImapCalendar(true);
          dispatch(setCalendarAccount(null));
        } else {
          dispatch(setCalendarAccount(calendarConfig));
        }
        const dataToken = await getToken();
        const token = dataToken.token.replace('/', '');
        switch (calendarConfig.provider) {
          case 'GOOGLE':
            setCalendarUrl(
              `${window.PATH_CL_GOO}/calendar/access/${dataToken.token}?prov=GO0&email=${
                calendarConfig.account ? calendarConfig.account : calendarConfig.email
              }`
            );
            break;
          case 'OUTLOOK':
            setCalendarUrl(`${window.PATH_CL_OUT}/calendar/access/${token}/?prov=OU0`);
            break;
          case 'IMAP':
            // Uncomment this section when NEXTCLOUD is launched
            // setCalendarUrl(
            // setCalendarUrl(`${window.PATH_CL_OUT}/calendar/access/${tokenData}/?prov=IM0`);
            // );
            break;
          default:
            break;
        }
      }
    } catch (error) {
      console.error('error load account:', error);
      dispatch(setCalendarAccount(null));
    }
  };

  useEffect(() => {
    loadAccount();
  }, []);

  const onRefreshCalendar = () => {
    setIndexCalendar(indexCalender + 1);
  };

  useEffect(() => {
    window.addEventListener('message', (event) => {
      const type = typeof event.data;
      try {
        if (type === 'string') {
          const data = JSON.parse(event.data);
          if (data.isLogout) {
            if (calendarAccount.provider === 'GOOGLE') {
              dispatch(setCalendarAccount(null));
            }
            if (calendarAccount.provider === 'OUTLOOK') {
              dispatch(setCalendarAccount(null));
            }
          }
        }
        if (type === 'string' && openFormModal) {
          const data = JSON.parse(event.data);
          if (data.actionCancelled) {
            setOpenFormModal(false);
            onRefreshCalendar();
          }
        }
      } catch (error) {
        // console.error(error);
      }
    });
    return () => {
      window.removeEventListener('message', {});
    };
  }, [openFormModal, calendarAccount]);

  const handleOpenModal = async () => {
    try {
      const dataToken = await getToken();
      const token = dataToken.token.replace('/', '');
      switch (calendarAccount.provider) {
        case 'GOOGLE':
          setCalendarEventUrl(
            `${window.PATH_CL_GOO}/calendar/?prov=GO0&email=${
              calendarAccount.account ? calendarAccount.account : calendarAccount.email
            }&appConnectEvent=${token}`
          );
          break;
        case 'OUTLOOK':
          setCalendarEventUrl(`${window.PATH_CL_OUT}/calendar/?prov=OU0&appConnectEvent=${token}`);
          break;
        case 'IMAP':
          // Uncomment this when NEXTCLOUD is launched
          // setCalendarEventUrl(`${window.PATH_CL_IMAP}/calendar/?prov=IM0&appConnectEvent=${data}`);
          break;
        default:
          break;
      }
      setOpenFormModal(true);
    } catch (error) {
      console.error('error lexon (handleOpenModal): ', error);
    }
  };

  const onCloseFormModal = () => {
    setOpenFormModal(false);
  };

  const onConfigAccount = async () => {
    try {
      const dataToken = await getToken();
      const token = dataToken.token.replace('/', '');
      window.open(`${window.PATH_CL_POR}/access/${token}/`, '_blank');
    } catch (error) {
      console.error('error lexon: ', error);
    }
  };

  const Title = () => {
    return (
      <Row className="row-title">
        <Col sm={12} className="ml-n3">
          <h3>{t('calendar_create_event')}</h3>
        </Col>
      </Row>
    );
  };

  return (
    <CalendarBackground
      className={classNames(['ml-background-dashboard background-box__flex-home', className])}
    >
      <div className="d-flex home__mb-notifi">
        <div aria-hidden="true">
          <LabelIcon
            label={t('Settings_calendar')}
            Icon={() => <CalendarIcon className="label-icon__notification" fill={colors.white} />}
            color={mainColor}
            className="ml-1 label-icon text-uppercase"
            classNameMain=""
            isDashboard
          />
        </div>

        {calendarAccount && (
          <div className="d-flex w-100 justify-content-end">
            <ButtonLabelIcon
              id="add-icon-calendar"
              text={t('general_button_new')}
              type="button"
              Icon={() => <AddIcon fill={colors.black} />}
              className="button-icon mr-1"
              onClick={handleOpenModal}
            />
            <ButtonLabelIcon
              id="refresh-icon-calendar"
              text={t('general_button_refresh')}
              type="button"
              Icon={() => <RefreshIcon fill={colors.black} />}
              className="button-icon"
              onClick={onRefreshCalendar}
            />
          </div>
        )}
      </div>
      {calendarAccount ? (
        <div className="mt-n4">
          <Iframe
            key={indexCalender}
            url={calendarUrl}
            width="100%"
            height="430px"
            id="calendarIframe"
            frameBorder="0"
            className="frame-calendar"
            allow="fullscreen"
            display="initial"
            position="relative"
          />
        </div>
      ) : (
        <>
          {!tokenLexonLoading ? (
            <div className="d-flex justify-content-center">
              <div className="box-calendar-sync">
                <div className="d-flex align-items-start mb-3">
                  <CalendarCheckIcon width={20} />
                  <span className="ml-2 title-calendars-sync">{t('calendar_calendars_sync')}</span>
                </div>
                {imapCalendar ? ( // Remove this when NEXTCLOUD is launched
                  <span className="info-account">{t('calendar_no_account_imap')}</span>
                ) : (
                  <span className="info-account">{t('calendar_no_account')}</span>
                )}
                <div className="btn-calendar-sync mt-3 mb-3">
                  <Button
                    className="text-uppercase"
                    type="button"
                    label={t('calendar_config_account')}
                    color="primary"
                    onClick={onConfigAccount}
                    disabled={imapCalendar} // Remove this when NEXTCLOUD is launched
                  />
                </div>
                <div className="d-flex justify-content-center">
                  <RefreshIcon width={15} height={20} />
                  <span className="refresh-calendars-sync ml-2">
                    {t('calendar_refresh_screen')}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="d-flex justify-content-center align-items-center">
              <Spinner />
            </div>
          )}
        </>
      )}
      {calendarAccount && (
        <div className="action-btn">
          <Button
            className="home__btn text-uppercase"
            type="submit"
            onClick={() => history.push('/Calendar')}
            label={t('general_see_all')}
            color="primary"
          />
        </div>
      )}
      <CalendarFormModal isOpen={openFormModal} toggle={onCloseFormModal} size="lg">
        <ModalHeader
          className="modal-header"
          backgroundColor={colors.transparent}
          onClose={onCloseFormModal}
        />
        <ModalBody>
          <div className="calendar-event-container">
            <Title />
            {openFormModal ? (
              <Iframe
                url={calendarEventUrl}
                width="100%"
                height="500px"
                id="form-iframe"
                frameBorder="0"
                display="initial"
                position="relative"
              />
            ) : null}
          </div>
        </ModalBody>
      </CalendarFormModal>
    </CalendarBackground>
  );
};

const CalendarBackground = styled(BackgroundBox)`
  #schedule_dialog_wrapper {
    min-height: 500px;
  }
  width: 90%;
  .box-calendar-sync {
    border: 1px solid black;
    height: auto;
    width: 70%;
    padding: 17px;
    .title-calendars-sync {
      font-family: ${fonts.default};
      font-weight: bold;
      font-size: ${`${fontSizes.md}px`};
    }
    .info-account {
      font-family: ${fonts.default};
    }
    .btn-calendar-sync {
      display: flex;
      justify-content: center;
      button {
        height: 42px;
        width: 181px;
      }
    }
    .refresh-calendars-sync {
      font-family: ${fonts.default};
      font-weight: bold;
      font-size: ${`${fontSizes.sm}px`};
    }
  }
`;

const CalendarFormModal = styled(Modal)`
  top: 15%;
  .modal-content {
    border-radius: 4px;
  }
  .modal-header {
    border: 0;
    height: 36px;
    padding-top: 20px;
    padding-bottom: 0;

    svg {
      fill: ${colors.suvaGrey} !important;
      height: 18px !important;
      width: 18px !important;
    }
  }
  .calendar-event-container {
    .row-title {
      border-bottom: 1px solid ${colors.spanishGray};
      padding-bottom: 17px;
      width: 100%;
      margin-left: 1px;
      h3 {
        color: ${colors.black};
        font-family: ${fonts.default};
        font-size: ${fontSizes.xlg}px;
        font-weight: bold;
        letter-spacing: 0;
        line-height: 24px;
      }
    }
  }
`;

CalendarSectionCard.defaultProps = {
  className: '',
};

CalendarSectionCard.propTypes = {
  className: PropTypes.string,
};

export default CalendarSectionCard;
